import { IdNodeRef, NodeBinding } from '@internal/open-catalog';
import { Box, Button, Chip, createStyles, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormHelperText, IconButton, List, ListItem, ListItemText, makeStyles, Switch, TextField, Theme, Typography } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import React, { useEffect, useRef, useState } from 'react';
import { MessagingNodeData } from '../flow';

export interface BindingDialogProps {
  open: boolean;
  data: MessagingNodeData;
  source: IdNodeRef;
  destination: IdNodeRef;
  onClose: (binding?: NodeBinding) => void;
};

interface Argument {
  key?: string;
  value?: string | number | boolean;
}

interface ChipData extends Argument {
  id: string | number;
}

const MatchAllChip: ChipData = {
  id: 'match', key: 'x-match', value: 'all'
}

const MatchAnyChip: ChipData = {
  id: 'match', key: 'x-match', value: 'any'
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    content: {
      paddingTop: 0,
      paddingBottom: 0
    },
    infoList: {
      '& > li': {
        padding: 0
      }
    },
    form: {
      display: 'flex',
      flexWrap: 'wrap',
      '& > div > div:first-child': {
        marginLeft: 0
      },
      '& > div > div:last-child': {
        marginRight: 0
      }
    },
    formSwitch: {
      width: '20ch',
    },
    formField: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
      width: '20ch',
    },
    chipContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      listStyle: 'none',
      padding: 0,
      paddingTop: theme.spacing(1),
      margin: 0,
      '& li:first-child div': {
        marginLeft: 0
      }
    },
    chip: {
      margin: theme.spacing(0.5),
    },
  })
);

export function BindingDialog(props: BindingDialogProps) {

  const classes = useStyles();

  const { onClose, open, data, source, destination } = props;

  const [matchAll, setMatchAll] = useState(true);
  const [chipData, setChipData] = useState<ChipData[]>([MatchAllChip]);

  const inputKeyRef = useRef<HTMLInputElement>();
  const inputValRef = useRef<HTMLInputElement>();
  const [argument, setArgument] = useState<Argument>();

  const emptyArgumentState = () => {
    setArgument(undefined);
    if (inputKeyRef && inputKeyRef.current) {
      inputKeyRef.current.value = '';
    }
    if (inputValRef && inputValRef.current) {
      inputValRef.current.value = '';
    }
  };

  const emptyState = () => {
    emptyArgumentState();
    setMatchAll(true);
    setChipData([MatchAllChip]);
  };

  const cancel = () => {
    onClose();
    emptyState();
  };

  const updateArgument = () => {
    const arg = argument || {};
    if (inputKeyRef && inputKeyRef.current) {
      arg.key = inputKeyRef.current.value?.trim();
    }
    if (inputValRef && inputValRef.current) {
      arg.value = inputValRef.current.value?.trim();
    }
    setArgument(arg);
    return arg;
  };

  const addArgument = () => {

    if (!argument || !argument.key || !argument.value) {
      return;
    }

    if (!chipData.find(chip => chip.key === argument.key && chip.value === argument.value)) {
      setChipData((chips) => [...chips, ...[{ id: chipData.length++, ...argument }]]);
    }

    emptyArgumentState();

    inputKeyRef?.current?.focus();
  };

  const submit = () => {

    const args: Record<string, string | number | boolean> = {};
    const binding: NodeBinding = {
      source: source.name,
      type: data.type,
      arguments: args
    };

    if (argument?.key && argument?.value || chipData.length > 1) {
      for (const chip of chipData) {
        if (chip.key) {
          args[chip.key] = chip.value || '';
        }
      }
    }

    if (argument?.key && argument?.value) {
      args[argument.key] = argument.value;
    }

    onClose(binding);
    emptyState();
  };

  const onMatchingChange = () => {
    chipData.shift();
    chipData.unshift(!matchAll ? MatchAllChip : MatchAnyChip);
    setMatchAll(!matchAll);
  }

  const handleDelete = (chipToDelete: ChipData) => () => {
    setChipData((chips) => chips.filter((chip) => chip.id !== chipToDelete.id));
    inputKeyRef?.current?.focus();
  };

  const onPressEnter = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      addArgument();
    }
  }

  useEffect(() => {
    setTimeout(() => inputKeyRef?.current?.focus(), 100)
  }, [open, inputKeyRef]);

  return (
    <Dialog open={open} fullWidth>
      <DialogTitle>New binding</DialogTitle>
      <DialogContent className={classes.content}>
        <List dense className={classes.infoList}>
          <ListItem>
            <ListItemText primary="Source" secondary={source.name} title={source.name} />
            <ListItemText primary="Destination" secondary={destination.name} title={destination.name} />
          </ListItem>
        </List>
        <Typography variant='subtitle2'>Arguments</Typography>
        <form className={classes.form} noValidate autoComplete="off">
          <FormControlLabel className={classes.formSwitch} label={matchAll ? "Matching all" : "Matching any"} control={
            <Switch checked={matchAll} onChange={onMatchingChange} />
          } />
          <div>
            <TextField required inputRef={inputKeyRef} className={classes.formField} size="small" label="Key" onChange={updateArgument} onKeyUp={onPressEnter} />
            <TextField required inputRef={inputValRef} className={classes.formField} size="small" label="Value" onChange={updateArgument} onKeyUp={onPressEnter} />
            <FormHelperText>Press 'enter' or use the 'add button' to insert the new argument.</FormHelperText>
          </div>
          <IconButton size='small' title="Add an argument" onClick={addArgument}>
            <AddIcon />
          </IconButton>
        </form>
        <Box component="ul" className={classes.chipContainer}>
          {chipData.map((chip) => {
            const value = chip.value ? `: ${chip.value}` : '';
            return (
              <li key={chip.id}>
                <Chip
                  label={`${chip.key}${value}`}
                  onDelete={chip.id === 'macth' ? undefined : handleDelete(chip)}
                  className={classes.chip}
                />
              </li>
            );
          })}
        </Box>
        {chipData.length === 1 && (
          <FormHelperText>
            When no arguments are defined the destination will accept all the incoming messages from: {source.name}
          </FormHelperText>
        )}
        {chipData.length > 1 && (
          <FormHelperText>
            The destination will accept only the messages that matches {matchAll ? 'all' : 'any'} of the arguments (aka. criterias) defined below.
          </FormHelperText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={cancel} color="primary">Cancel</Button>
        <Button onClick={submit} color="secondary">Submit</Button>
      </DialogActions>
    </Dialog>
  );
};
